import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Zap, Battery, Gauge, Clock, Building2, Users, 
  CreditCard, Smartphone, Car, ArrowRight, CheckCircle2,
  Shield, Globe, Leaf
} from 'lucide-react';

const ServicesPage = () => {
  const chargingTypes = [
    {
      icon: Battery,
      title: 'Karikim AC Normal',
      power: '22 kW',
      time: '3-4 orë',
      description: 'Ideal për karikim gjatë natës ose parkime të gjata. Përshtatshëm për përdorim ditor.',
      features: ['Kosto më e ulët', 'I përshtatshëm për shtëpi', 'Mirëmbajtje e thjeshtë'],
      color: 'blue'
    },
    {
      icon: Zap,
      title: 'Karikim DC Fast',
      power: '60-180 kW',
      time: '25-45 min',
      description: 'Zgjidhja e përsosur për udhëtime të gjata dhe nevoja urgjente karikimi.',
      features: ['Karikim i shpejtë', 'Ideal për autostrada', 'Mbështet të gjitha EV-të'],
      color: 'indigo'
    },
    {
      icon: Gauge,
      title: 'Karikim Ultra-Fast',
      power: 'Mbi 240 kW',
      time: '15-20 min',
      description: 'Teknologjia më e avancuar për karikimin më të shpejtë të mundshëm.',
      features: ['Koha minimale', 'Teknologji e fundit', 'Fuqi maksimale'],
      color: 'lime'
    }
  ];

  const businessServices = [
    {
      icon: Building2,
      title: 'Zgjidhje për Kompani',
      description: 'Instalim dhe menaxhim i plotë i infrastrukturës së karikimit për ambientet tuaja të punës.'
    },
    {
      icon: Car,
      title: 'Menaxhim Flotash',
      description: 'Platforma e dedikuar për monitorimin dhe optimizimin e karikimit të flotave elektrike.'
    },
    {
      icon: Users,
      title: 'Zgjidhje Retail',
      description: 'Stacione karikimi për qendra tregtare, hotele dhe biznese të tjera që presin klientë.'
    }
  ];

  const compatibility = [
    'Tesla', 'BMW', 'Mercedes', 'Audi', 'Volkswagen', 'Porsche',
    'Hyundai', 'Kia', 'Nissan', 'Renault', 'Peugeot', 'Fiat'
  ];

  return (
    <div data-testid="services-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-lime-400 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-blue-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-sm font-medium uppercase tracking-widest text-lime-400 mb-4">
              Shërbimet Tona
            </span>
            <h1 
              data-testid="services-title"
              className="text-4xl md:text-6xl font-bold font-['Inter_Tight'] tracking-tight mb-6"
            >
              Energji Elektrike për<br />
              <span className="text-lime-400">Çdo Nevojë</span>
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed mb-8">
              Nga karikimi normal ditor deri tek ultra-fast për udhëtime të gjata. 
              AlbMove ofron zgjidhje të plota për individë dhe biznese.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                data-testid="services-cta-stations"
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
              >
                <Link to="/stations">
                  Gjej Stacion
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button 
                asChild
                data-testid="services-cta-contact"
                variant="outline"
                className="bg-transparent border-2 border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg"
              >
                <Link to="/contact">
                  Kontakt Biznesi
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Charging Types Section */}
      <section data-testid="charging-types-section" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium uppercase tracking-widest text-blue-600 mb-4">
              Llojet e Karikimit
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 font-['Inter_Tight'] tracking-tight">
              Zgjidhni Sipas Nevojës
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {chargingTypes.map((type, index) => (
              <div 
                key={index}
                data-testid={`charging-type-card-${index}`}
                className="bg-slate-50 rounded-3xl p-8 hover:shadow-xl transition-all duration-300 group"
              >
                <div className={`w-16 h-16 rounded-2xl bg-${type.color}-100 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  <type.icon className={`w-8 h-8 text-${type.color}-600`} />
                </div>
                
                <h3 className="text-2xl font-semibold text-slate-900 font-['Inter_Tight'] mb-2">
                  {type.title}
                </h3>
                
                <div className="flex items-center gap-4 mb-4">
                  <span className="inline-flex items-center gap-1 text-blue-700 font-semibold">
                    <Zap className="w-4 h-4" />
                    {type.power}
                  </span>
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    <Clock className="w-4 h-4" />
                    {type.time}
                  </span>
                </div>
                
                <p className="text-slate-500 leading-relaxed mb-6">
                  {type.description}
                </p>
                
                <ul className="space-y-2">
                  {type.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Methods */}
      <section data-testid="payment-section" className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-sm font-medium uppercase tracking-widest text-blue-600 mb-4">
                Metodat e Pagesës
              </span>
              <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 font-['Inter_Tight'] tracking-tight mb-6">
                Pagesa e Thjeshtë dhe Transparente
              </h2>
              <p className="text-lg text-slate-500 leading-relaxed mb-8">
                Zgjidhni metodën që ju përshtatet më mirë. Çmimet janë të qarta dhe pa surpriza të pakëndshme.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Smartphone, title: 'Aplikacion Mobile', desc: 'Paguani direkt nga telefoni juaj' },
                  { icon: CreditCard, title: 'Kartë RFID', desc: 'Prekni dhe filloni karikimin' },
                  { icon: CreditCard, title: 'Kartë Bankare', desc: 'Paguani me kartë krediti/debiti' }
                ].map((method, index) => (
                  <div 
                    key={index}
                    data-testid={`payment-method-${index}`}
                    className="flex items-center gap-4 bg-white rounded-2xl p-5 border border-slate-200/50"
                  >
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <method.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{method.title}</h3>
                      <p className="text-sm text-slate-500">{method.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-200/50">
              <h3 className="text-2xl font-semibold text-slate-900 font-['Inter_Tight'] mb-6">
                Metodat e Pagesës
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Smartphone className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Aplikacion Mobile</p>
                    <p className="text-sm text-slate-500">Paguani direkt nga telefoni juaj</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 pb-4 border-b border-slate-100">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Kartë RFID</p>
                    <p className="text-sm text-slate-500">Prekni dhe filloni karikimin</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Kartë Bankare</p>
                    <p className="text-sm text-slate-500">Paguani me kartë krediti/debiti</p>
                  </div>
                </div>
              </div>
              
              <p className="text-xs text-slate-400 mt-6">
                * Kontrolloni aplikacionin për çmimet aktuale të karikimit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Business Services */}
      <section data-testid="business-services-section" className="section-padding text-white" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="inline-block text-sm font-medium uppercase tracking-widest text-lime-400 mb-4">
              Për Biznese
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold font-['Inter_Tight'] tracking-tight">
              Zgjidhje Profesionale
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {businessServices.map((service, index) => (
              <div 
                key={index}
                data-testid={`business-service-${index}`}
                className="bg-blue-800/50 rounded-3xl p-8 hover:bg-blue-800 transition-colors"
              >
                <service.icon className="w-12 h-12 text-lime-400 mb-6" />
                <h3 className="text-xl font-semibold font-['Inter_Tight'] mb-3">
                  {service.title}
                </h3>
                <p className="text-blue-200 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center">
            <Button 
              asChild
              data-testid="business-contact-button"
              className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact">
                Kërkoni Ofertë për Biznes
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Compatibility */}
      <section data-testid="compatibility-section" className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-medium uppercase tracking-widest text-blue-600 mb-4">
              Përshtatshmëri
            </span>
            <h2 className="text-4xl md:text-5xl font-semibold text-slate-900 font-['Inter_Tight'] tracking-tight mb-4">
              Të Gjitha Markat
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              Karikuesit tanë mbështesin të gjitha automjetet elektrike dhe plug-in hybrid në treg.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {compatibility.map((brand, index) => (
              <span 
                key={index}
                data-testid={`brand-${index}`}
                className="px-6 py-3 bg-slate-100 rounded-full text-slate-700 font-medium hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-default"
              >
                {brand}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: 'I Sigurt', desc: 'Teknologji e certifikuar' },
              { icon: Globe, title: '24/7', desc: 'Gjithmonë i disponueshëm' },
              { icon: Leaf, title: 'Eko', desc: 'Energji e pastër' },
              { icon: Users, title: 'Mbështetje', desc: 'Ndihmë kur keni nevojë' }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 text-center border border-slate-200/50">
                <item.icon className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-gradient-to-br from-lime-400 to-lime-500 rounded-3xl p-12 md:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-4">
              Gati për të Filluar?
            </h2>
            <p className="text-lg text-slate-700 mb-8 max-w-xl mx-auto">
              Shkarkoni aplikacionin dhe gjeni stacionin më të afërt tani.
            </p>
            <Button 
              asChild
              data-testid="services-final-cta"
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/stations">
                Shiko Stacionet
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
